import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import type { LucideIcon } from "lucide-react";
import { getRouteByKey, isRouteAvailable, normalizeSiteLanguage, type SiteLanguage } from "@/utils/routing";
import { QUIZZES } from "@/utils/rateQuiz/quizzes";
import { siteQuizzesFor } from "@/utils/rateQuiz/siteQuizzes";
import { quizRouteKey, tradeForPath } from "@/utils/rateQuiz/routes";

export interface QuizLink {
  key: string;
  icon: LucideIcon;
  path: string;
  label: string;
}

/**
 * The live quizzes for the current language as links, shared by the top-bar
 * ticker and the header menu. On a trade's pages each quiz deep-links to that
 * trade's test; elsewhere to the quiz's hub. `hub` is the all-tests page.
 */
export const useQuizLinks = (): { lang: SiteLanguage; hub: string | null; items: QuizLink[] } => {
  const { t, i18n } = useTranslation();
  const lang = normalizeSiteLanguage(i18n.language);
  const { pathname } = useLocation();
  const hub = isRouteAvailable("quizHub", lang) ? getRouteByKey("quizHub", lang) : null;
  const trade = tradeForPath(pathname, lang);
  const items = QUIZZES.flatMap(({ key, icon, routeKey }) => {
    if (!routeKey || !isRouteAvailable(routeKey, lang)) return [];
    const tradeKey = trade ? quizRouteKey(routeKey, trade) : null;
    const path = getRouteByKey(tradeKey && isRouteAvailable(tradeKey, lang) ? tradeKey : routeKey, lang);
    return [{ key, icon, path, label: t(`sections.rateQuiz.bubble.items.${key}`) }];
  });
  // One-site quizzes (not per trade) carry their own copy and always link to their page.
  for (const { key, icon, routeKey, menuLabel } of siteQuizzesFor(lang)) {
    items.push({ key, icon, path: getRouteByKey(routeKey, lang), label: menuLabel });
  }
  return { lang, hub, items };
};
